import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';

export type CollectionHealthStatus =
  | 'not_started'
  | 'on_track'
  | 'behind'
  | 'at_risk'
  | 'critical';

export interface CollectionHealth {
  status: CollectionHealthStatus;
  actual: number;
  expected: number;
}

export interface DailyCollectionEntry {
  paymentDate: string;
  amount: number;
}

export interface DashboardData {
  students?: number;
  newAdmissions?: number;
  withdrawnStudents?: number;
  teachers?: number;
  staff?: number;
  classes?: number;
  // Core collection numbers
  totalBilled?: number;        // Net expected to collect this term (after adjustments)
  totalCollected?: number;     // Total cash received (includes advance credits)
  outstandingFees?: number;    // Still owed by active students
  advanceCredits?: number;     // Cash received beyond billed amount (overpayments)
  // Informational adjustment costs
  scholarships?: number;
  totalDiscounts?: number;
  totalWriteoffs?: number;
  // Collection health
  collectionRate?: number;
  collectionHealth?: CollectionHealth;
  // Item sales (non-subscription purchases)
  itemSalesBilled?: number;
  itemSalesCollected?: number;
  itemSalesOutstanding?: number;
  // Subscription detail
  subscriptionFees?: number;
  subscriptionCollectedBreakdown?: Array<{
    name: string;
    collected?: number;
  }>;
  subscriptionOutstandingBreakdown?: Array<{
    name: string;
    outstanding?: number;
  }>;
  // Percentages (computed by API, not frontend)
  outstandingPercentage?: number;
  adjustmentsPercentage?: number;
  // Student cohort metrics
  numberOwing?: number;
  zeroPayments?: number;
  zeroPaymentOutstanding?: number;
  // Daily collection series (last 30 days, excludes discounts/write-offs/zero entries)
  dailyCollections?: DailyCollectionEntry[];
  payments?: Array<{
    studentId: number;
    student: string;
    paymentDate?: string | null;
    amount: number;
    isDigital?: boolean;
  }>;
}

export const DashboardApi = createApi({
  reducerPath: 'DashboardApi',
  baseQuery,
  tagTypes: ['Dashboard'],
  endpoints: builder => ({
    getDashboardData: builder.query<DashboardData, void>({
      query: () => `/dashboard/data`,
      providesTags: ['Dashboard'],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useGetDashboardDataQuery } = DashboardApi;
